import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Reveal from '@/components/Reveal';

/**
 * Universal Hero — mango.org-style full-bleed hero with photo OR looping video.
 *
 * Layers (back → front):
 *   1. Full-bleed background MEDIA: `bgVideo` (autoplay/muted/loop, with `bgPoster`)
 *      takes precedence over `bgImage`. Both are lightly softened.
 *   2. Soft warm tint overlay (`block` prop).
 *   3. Side spotlight gradient for left-aligned text.
 *   4. Bottom vignette.
 *   5. Faint grain.
 *   6. Content (eyebrow, headline, subtitle, CTAs).
 */
export default function Hero({
  eyebrow,
  title,
  subtitle,
  ctas = [],
  block = 'cream',
  bgImage,
  bgVideo,
  bgPoster,
  align = 'left',
  size = 'lg',
  testId = 'page-hero',
  // legacy / unused — kept so we don't have to touch every page
  // eslint-disable-next-line no-unused-vars
  rightImage,
  // eslint-disable-next-line no-unused-vars
  imageBlock,
  // eslint-disable-next-line no-unused-vars
  rightAccent,
  // eslint-disable-next-line no-unused-vars
  leftAccent,
  // eslint-disable-next-line no-unused-vars
  illustrationOverride,
}) {
  const reduce = useReducedMotion();

  // Overlay tint colors — gentler so the photo reads through. Lighter on cream/cream-deep
  // (where the photo should be the star), heavier on dark/colored blocks (for legibility).
  const overlay = {
    'cream':      'bg-[hsl(var(--c-cream)/0.32)]',
    'cream-deep': 'bg-[hsl(var(--c-cream-deep)/0.40)]',
    'orange':     'bg-[hsl(var(--c-mango-orange)/0.55)]',
    'yellow':     'bg-[hsl(var(--c-mango-yellow)/0.50)]',
    'leaf':       'bg-[hsl(var(--c-leaf)/0.62)]',
    'teal':       'bg-[hsl(var(--c-teal)/0.62)]',
    'red':        'bg-[hsl(var(--c-mango-red)/0.58)]',
    'ink':        'bg-[hsl(var(--c-ink)/0.62)]',
  }[block] || 'bg-[hsl(var(--c-cream)/0.32)]';

  // Bottom vignette gradient — fades to the overlay tone for readability.
  const vignette = {
    'cream':      'from-transparent via-transparent to-[hsl(var(--c-cream)/0.85)]',
    'cream-deep': 'from-transparent via-transparent to-[hsl(var(--c-cream-deep)/0.92)]',
    'orange':     'from-transparent via-transparent to-[hsl(var(--c-mango-orange-deep)/0.85)]',
    'yellow':     'from-transparent via-transparent to-[hsl(var(--c-mango-yellow-deep)/0.78)]',
    'leaf':       'from-transparent via-transparent to-[hsl(var(--c-leaf-deep)/0.92)]',
    'teal':       'from-transparent via-transparent to-[hsl(var(--c-teal-deep)/0.92)]',
    'red':        'from-transparent via-transparent to-[hsl(var(--c-mango-red-deep)/0.92)]',
    'ink':        'from-transparent via-transparent to-[hsl(var(--c-ink)/0.92)]',
  }[block] || 'from-transparent via-transparent to-[hsl(var(--c-cream)/0.85)]';

  // Left-side spotlight gradient — heavier on the text side so copy stays readable,
  // fading to transparent on the right so the photo shines.
  const spotlight = {
    'cream':      'from-[hsl(var(--c-cream)/0.86)] via-[hsl(var(--c-cream)/0.50)] to-transparent',
    'cream-deep': 'from-[hsl(var(--c-cream-deep)/0.88)] via-[hsl(var(--c-cream-deep)/0.55)] to-transparent',
    'orange':     'from-[hsl(var(--c-mango-orange-deep)/0.58)] via-[hsl(var(--c-mango-orange-deep)/0.30)] to-transparent',
    'yellow':     'from-[hsl(var(--c-mango-yellow-deep)/0.55)] via-[hsl(var(--c-mango-yellow-deep)/0.28)] to-transparent',
    'leaf':       'from-[hsl(var(--c-leaf-deep)/0.62)] via-[hsl(var(--c-leaf-deep)/0.32)] to-transparent',
    'teal':       'from-[hsl(var(--c-teal-deep)/0.62)] via-[hsl(var(--c-teal-deep)/0.32)] to-transparent',
    'red':        'from-[hsl(var(--c-mango-red-deep)/0.58)] via-[hsl(var(--c-mango-red-deep)/0.30)] to-transparent',
    'ink':        'from-[hsl(var(--c-ink)/0.72)] via-[hsl(var(--c-ink)/0.36)] to-transparent',
  }[block] || 'from-[hsl(var(--c-cream)/0.86)] via-[hsl(var(--c-cream)/0.50)] to-transparent';

  // Accent stripe color (thin bars top + bottom for the editorial block feel)
  const accentTop = {
    'cream':      'bg-[hsl(var(--c-mango-yellow))]',
    'cream-deep': 'bg-[hsl(var(--c-mango-orange))]',
    'orange':     'bg-[hsl(var(--c-mango-yellow))]',
    'yellow':     'bg-[hsl(var(--c-mango-orange))]',
    'leaf':       'bg-[hsl(var(--c-mango-yellow))]',
    'teal':       'bg-[hsl(var(--c-mango-yellow))]',
    'red':        'bg-[hsl(var(--c-mango-yellow))]',
    'ink':        'bg-[hsl(var(--c-mango-orange))]',
  }[block];
  const accentBottom = {
    'cream':      'bg-[hsl(var(--c-mango-red))]',
    'cream-deep': 'bg-[hsl(var(--c-leaf))]',
    'orange':     'bg-[hsl(var(--c-mango-red))]',
    'yellow':     'bg-[hsl(var(--c-leaf))]',
    'leaf':       'bg-[hsl(var(--c-mango-orange))]',
    'teal':       'bg-[hsl(var(--c-mango-orange))]',
    'red':        'bg-[hsl(var(--c-mango-yellow))]',
    'ink':        'bg-[hsl(var(--c-mango-red))]',
  }[block];

  const isDarkBlock = ['orange', 'leaf', 'teal', 'red', 'ink'].includes(block);
  const eyebrowColor = isDarkBlock ? 'text-white/90' : 'text-[hsl(var(--c-ink))]/70';
  const titleColor = isDarkBlock ? 'text-white' : 'text-[hsl(var(--c-ink))]';
  const subColor = isDarkBlock ? 'text-white/90' : 'text-[hsl(var(--c-ink-soft))]';

  const minH = size === 'md'
    ? 'min-h-[460px] sm:min-h-[520px] lg:min-h-[560px]'
    : 'min-h-[560px] sm:min-h-[640px] lg:min-h-[720px]';

  const alignClass = align === 'center' ? 'items-center text-center' : 'items-start text-left';

  return (
    <section
      data-testid={testId}
      className={`relative w-full overflow-hidden border-b-2 border-[hsl(var(--c-ink))] ${minH}`}
    >
      {/* 1) Full-bleed background — video takes precedence over image */}
      {bgVideo ? (
        <motion.div
          className="hero-bg-video-full"
          initial={reduce ? false : { scale: 1.10, opacity: 0 }}
          animate={reduce ? {} : { scale: 1.05, opacity: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden="true"
        >
          <video
            src={bgVideo}
            poster={bgPoster || bgImage}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="hero-bg-video-el"
          />
        </motion.div>
      ) : bgImage && (
        <motion.div
          className="hero-bg-image-full"
          style={{ backgroundImage: `url(${bgImage})` }}
          initial={reduce ? false : { scale: 1.15, opacity: 0 }}
          animate={reduce ? {} : { scale: 1.08, opacity: 1 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden="true"
        />
      )}

      {/* 2) Soft tint overlay so text remains readable */}
      <div className={`absolute inset-0 ${overlay}`} aria-hidden="true" />

      {/* 2b) Side spotlight gradient for left-aligned text (mango.org-style) */}
      {align !== 'center' && (
        <div
          className={`absolute inset-0 bg-gradient-to-r ${spotlight}`}
          aria-hidden="true"
        />
      )}

      {/* 3) Bottom gradient vignette */}
      <div
        className={`absolute inset-0 bg-gradient-to-b ${vignette}`}
        aria-hidden="true"
      />

      {/* 4) Faint grain for editorial texture */}
      <div className="absolute inset-0 grain" aria-hidden="true" />

      {/* 5) Top + bottom accent stripes */}
      <div className={`absolute top-0 left-0 right-0 h-[6px] ${accentTop}`} aria-hidden="true" />
      <div className={`absolute bottom-0 left-0 right-0 h-[6px] ${accentBottom}`} aria-hidden="true" />

      {/* 6) Content */}
      <div className={`relative mx-auto max-w-[1400px] px-5 sm:px-10 lg:px-16 py-20 sm:py-24 lg:py-32 flex flex-col justify-center ${alignClass} ${minH}`}>
        <div className={`w-full ${align === 'center' ? 'max-w-3xl mx-auto' : 'max-w-3xl'}`}>
          {eyebrow && (
            <Reveal>
              <div className={`eyebrow ${eyebrowColor}`}>{eyebrow}</div>
            </Reveal>
          )}
          <Reveal delay={0.05}>
            <h1 className={`display-hero mt-4 sm:mt-5 display-xl ${titleColor}`}>
              {title}
            </h1>
          </Reveal>
          {subtitle && (
            <Reveal delay={0.1}>
              <p className={`mt-5 sm:mt-6 ${align === 'center' ? 'mx-auto' : ''} max-w-2xl text-base sm:text-lg lg:text-xl leading-relaxed ${subColor}`}>
                {subtitle}
              </p>
            </Reveal>
          )}
          {ctas.length > 0 && (
            <Reveal delay={0.15}>
              <div className={`mt-8 sm:mt-10 flex flex-wrap gap-0 ${align === 'center' ? 'justify-center' : ''}`}>
                {ctas.map((cta, i) => (
                  <HeroCta key={i} cta={cta} index={i} />
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

function HeroCta({ cta, index }) {
  const variantClass = {
    orange: 'bg-grad-mango hover:saturate-110 text-white',
    leaf: 'bg-grad-forest-soft hover:saturate-125 text-white',
    teal: 'bg-grad-emerald hover:saturate-125 text-white',
    red: 'bg-grad-sunset hover:saturate-125 text-white',
    yellow: 'bg-[hsl(var(--c-mango-yellow))] hover:bg-[hsl(41_100%_55%)] text-[hsl(var(--c-ink))]',
    ink: 'bg-grad-forest hover:saturate-125 text-white',
    cream: 'bg-white hover:bg-[hsl(var(--c-cream))] text-[hsl(var(--c-ink))]',
    emerald: 'bg-grad-emerald hover:saturate-125 text-white',
    forest: 'bg-grad-forest hover:saturate-125 text-white',
  }[cta.variant || 'orange'];

  const base = `btn-square btn-transition inline-flex items-center justify-center h-12 sm:h-14 px-5 sm:px-7 text-xs sm:text-sm font-bold uppercase tracking-wider border-2 border-[hsl(var(--c-ink))] ${variantClass} ${index > 0 ? '-ml-[2px]' : ''}`;

  const inner = (
    <>
      {cta.icon && <span className="mr-2 inline-flex">{cta.icon}</span>}
      {cta.label}
    </>
  );

  if (cta.to) {
    return <Link to={cta.to} data-testid={cta.testId} className={base}>{inner}</Link>;
  }
  return <a href={cta.href || '#'} data-testid={cta.testId} className={base}>{inner}</a>;
}
